import React from "react";
import { Grid, Typography, Box, Paper, Divider } from "@mui/material";
import Plot from "react-plotly.js";

function getAnnotations(values, label, unit = "") {
  const validValues = values.filter((v) => typeof v === "number" && !isNaN(v));
  if (validValues.length === 0) return [];

  const sorted = [...validValues].sort((a, b) => a - b);
  const min = sorted[0];
  const max = sorted[sorted.length - 1];
  const median = sorted[Math.floor(sorted.length / 2)];

  return [
    {
      x: label,
      y: min,
      text: `Min: ${min.toFixed(2)}${unit}`,
      showarrow: false,
      yshift: -15,
      font: { size: 11, color: "#34495e" },
    },
    {
      x: label,
      y: median,
      text: `Median: ${median.toFixed(2)}${unit}`,
      showarrow: false,
      yshift: 0,
      font: { size: 11, color: "#34495e" },
    },
    {
      x: label,
      y: max,
      text: `Max: ${max.toFixed(2)}${unit}`,
      showarrow: false,
      yshift: 15,
      font: { size: 11, color: "#34495e" },
    },
  ];
}

export default function SpeedDurationBoxPlot({ data }) {
  if (!data || Object.keys(data).length === 0) {
    return (
      <Box mt={3}>
        <Typography variant="h6" color="textSecondary">
          No data available to display box plots.
        </Typography>
      </Box>
    );
  }
  return (
    <Box mt={3}>
      {Object.entries(data).map(([billboardId, values]) => {
        const speedsInKmh = values.speeds.map((v) =>
          typeof v === "number" ? v * 3.6 : v
        );
        const speedAnnotations = getAnnotations(speedsInKmh, "Speed", " km/h");
        const durationAnnotations = getAnnotations(
          values.durations,
          "Duration",
          " sec"
        );

        return (
          <Paper
            key={billboardId}
            elevation={4}
            sx={{
              p: 3,
              mb: 4,
              borderRadius: 3,
              backgroundColor: "#f9f9f9",
            }}
          >
            <Typography
              variant="h6"
              sx={{ mb: 2, color: "primary.main", fontWeight: 600 }}
            >
              ID: {billboardId}, {values.location}, {values.height} X{" "}
              {values.width}
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    backgroundColor: "#fff",
                    p: 2,
                    borderRadius: 2,
                    boxShadow: 2,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{ mb: 1, color: "grey.700" }}
                  >
                    Speed Box Plot
                  </Typography>
                  <Plot
                    data={[
                      {
                        y: speedsInKmh,
                        type: "box",
                        name: "Speed (km/h)",
                        marker: { color: "#1976d2" },
                      },
                    ]}
                    layout={{
                      margin: { t: 20, l: 40, r: 20, b: 20 },
                      height: 300,
                      paper_bgcolor: "#fff",
                      plot_bgcolor: "#fff",
                      annotations: speedAnnotations,
                    }}
                    config={{ displayModeBar: false }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    backgroundColor: "#fff",
                    p: 2,
                    borderRadius: 2,
                    boxShadow: 2,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{ mb: 1, color: "grey.700" }}
                  >
                    Duration Box Plot
                  </Typography>
                  <Plot
                    data={[
                      {
                        y: values.durations,
                        type: "box",
                        name: "Duration",
                        marker: { color: "#2e7d32" },
                      },
                    ]}
                    layout={{
                      margin: { t: 20, l: 40, r: 20, b: 20 },
                      height: 300,
                      paper_bgcolor: "#fff",
                      plot_bgcolor: "#fff",
                      annotations: durationAnnotations,
                    }}
                    config={{ displayModeBar: true, displaylogo: false }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Paper>
        );
      })}
    </Box>
  );
}
